import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CalendarDay, TimetableDay } from '@versita/shared/models';
import {
  academicYearFromDate,
  adjustDayByOne,
  adjustMonthByOne,
  adjustWeekByOne,
  getCalendarDays,
  getCurrentAcademicYear,
  getTimetableDay,
  getTimetableWeekDays,
  getWeekStartDateNumber,
  getWeekStartNumberDate,
  parseSelectedDay,
  parseWeekStart,
  startOf,
  toMonthStartFormat,
  toSelectedDayFormat,
  toWeekStartFormat,
} from '@versita/shared/timetable-utils';
import type {
  PeriodAdjustor,
  TimetableCategories,
} from '@versita/shared/timetable-utils';
import { DateTime } from 'luxon';
import { pipe } from 'ramda';

export const INCREMENT = 'INCREMENT' as const;
export const DECREMENT = 'DECREMENT' as const;
export type AdjustmentType = typeof INCREMENT | typeof DECREMENT;
const dateTimeAdjustment = {
  [INCREMENT]: 'plus',
  [DECREMENT]: 'minus',
} as const;

export type TimetableViewMode = 'WEEK' | 'DAY';

/** State slice for calendar and timetables */
export interface TimetableState {
  /** The Id of the current timetable (either a course instance Id or a student network ID) */
  timetableId?: string;
  /** The date of the first day of the week in the timetable in WEEK_START_FORMAT */
  timetableWeekStart: string;
  /** The current academic year (e.g. 2022.2023) */
  academicYear: string;
  /** The numeric number of the academic year week (0-52). Corresponds to the week start date */
  timetableWeekNumber: number;
  /** A collection of all the categories available for events */
  eventCategories: TimetableCategories;
  /** The days to show in the timetable (not the events) */
  timetableDays: TimetableDay[];
  /** The date of the first day of the month in the calendar in MONTH_START_FORMAT */
  calendarMonthStart: string;
  /** The days to show in the calendar */
  calendarDays: CalendarDay[];
  /** The currently selected day in SELECTED_DAY_FORMAT */
  selectedDay?: string | null;
  /** The current view mode of the timetable (e.g. week or day) */
  viewMode: TimetableViewMode;
  /** Whether the calendar is currently toggled visible (mobile only, it's
   * alaways visible on desktop) */
  calendarVisible: boolean;
  /** Whether the modal that shows the ICS URL for the current timetable is currently toggled visible */
  icsUrlModalVisible: boolean;
  /** The timezone: 'default' is local time, 'Europe/London' for UK time */
  timezone: string;
  /** Whether the student has been redirected to a course timetable from their (unavailable) personal timetable */
  redirectedToCourseTimetable: boolean;
  currentPersonalTimetableAvailable: boolean;
}

// Initial State
const {
  calendarMonthStart,
  timetableWeekStart,
  timetableWeekNumber,
  selectedDay,
} = getDefaults();
const initialState: TimetableState = {
  timetableWeekStart,
  timetableWeekNumber,
  selectedDay,
  eventCategories: {},
  academicYear: getCurrentAcademicYear(),
  timetableDays: window.location
    ? getTimetableWeekDays(timetableWeekStart)
    : [getTimetableDay(selectedDay)],
  calendarMonthStart,
  calendarDays: getCalendarDays(calendarMonthStart),
  viewMode: window.location ? 'WEEK' : 'DAY',
  calendarVisible: false,
  icsUrlModalVisible: false,
  timezone: 'Europe/London',
  redirectedToCourseTimetable: false,
  currentPersonalTimetableAvailable: false,
};

// Reducers and Actions
export const timetableSlice = createSlice({
  name: 'timetable',
  initialState,
  reducers: {
    setTimetableId(state, { payload }: PayloadAction<string>) {
      if (state.timetableId !== payload) {
        state.timetableId = payload;
      }
    },
    changeViewMode(state, { payload }: PayloadAction<TimetableViewMode>) {
      const setMode = {
        DAY: setDayMode,
        WEEK: setWeekMode,
      }[payload];

      setMode(state);
    },
    selectCalendarDay(state, { payload }: PayloadAction<string>) {
      const monthStart = parseSelectedDay(payload).startOf('month');
      setSelectedDay(payload, state);
      state.calendarMonthStart = toMonthStartFormat(monthStart);
      state.calendarDays = getCalendarDays(state.calendarMonthStart);
    },
    selectTimetableWeek(state, { payload }: PayloadAction<string>) {
      setSelectedWeek(payload, state);
    },
    setTimetableWeekNumber(state, { payload }: PayloadAction<number>) {
      if (isNaN(payload) || payload < 0 || payload > 51) {
        return;
      }
      const weekStart = getWeekStartNumberDate(payload, state.academicYear);
      setSelectedWeek(weekStart, state);
    },
    refreshCategories(
      state,
      { payload }: PayloadAction<TimetableCategories[string]>,
    ) {
      const context = state.timetableId;
      if (context) {
        state.eventCategories[context] = {
          ...payload,
          ...(state.eventCategories[context] || {}),
        };
      }
    },
    setCalendarVisibility(state, { payload }: PayloadAction<boolean>) {
      state.calendarVisible = payload;
    },
    setIcsUrlModalVisibility(state, { payload }: PayloadAction<boolean>) {
      state.icsUrlModalVisible = payload;
    },
    toggleCategoryVisibility(state, { payload }: PayloadAction<string>) {
      const context = state.timetableId;
      if (context) {
        state.eventCategories[context][payload].visible =
          !state.eventCategories[context][payload].visible;
      }
    },
    setTimezone(state, { payload }: PayloadAction<string>) {
      state.timezone = payload;
    },
    setIsRedirectedToCourseTimetable(
      state,
      { payload }: PayloadAction<boolean>,
    ) {
      state.redirectedToCourseTimetable = payload;
    },
    setIsCurrentPersonalTimetableAvailable(
      state,
      { payload }: PayloadAction<boolean>,
    ) {
      state.currentPersonalTimetableAvailable = payload;
    },
    adjustTimetableDay: withAdjustor(adjustDayByOne, (state, adjust) => {
      const newDay = adjust(
        state.selectedDay || toSelectedDayFormat(DateTime.now()),
      );
      setSelectedDay(newDay, state);
    }),
    adjustTimetableWeek: withAdjustor(adjustWeekByOne, (state, adjust) => {
      const newWeekStart = adjust(state.timetableWeekStart);
      setSelectedWeek(newWeekStart, state);
    }),
    adjustCalendarMonth: withAdjustor(adjustMonthByOne, (state, adjust) => {
      state.calendarMonthStart = adjust(state.calendarMonthStart);
      state.calendarDays = getCalendarDays(state.calendarMonthStart);
    }),
  },
});

/**
 * HoF to standardise reducers that handle (inc/dec)rementing dates
 */
function withAdjustor(adjustorFn: PeriodAdjustor, reducer: AdjustmentReducer) {
  return (
    state: TimetableState,
    { payload }: PayloadAction<AdjustmentType>,
  ) => {
    const adjustmentType = dateTimeAdjustment[payload];
    const makeAdjustment = adjustorFn(adjustmentType);
    reducer(state, makeAdjustment);
  };
}
type AdjustmentReducer = (
  state: TimetableState,
  makeAdjustment: ReturnType<PeriodAdjustor>,
) => void;

// There are verious interdependencies between thee different state actions.
// e.g. we want to also set the selected week when a day is selected.
// They are abstracted here to reduce the noise

const setSelectedWeek = (selectedWeek: string, state: TimetableState) => {
  state.timetableWeekStart = selectedWeek;
  state.timetableWeekNumber = getWeekStartDateNumber(selectedWeek);
  state.academicYear = academicYearFromDate(selectedWeek);
  setWeekMode(state);
};

function setWeekMode(state: TimetableState) {
  state.timetableDays = getTimetableWeekDays(state.timetableWeekStart);
  state.viewMode = 'WEEK';
  state.selectedDay = null;
}

function setSelectedDay(selectedDay: string, state: TimetableState) {
  state.selectedDay = selectedDay;
  state.timetableWeekStart = pipe(
    parseSelectedDay,
    startOf('week'),
    toWeekStartFormat,
  )(selectedDay);
  state.timetableWeekNumber = getWeekStartDateNumber(state.timetableWeekStart);
  state.academicYear = academicYearFromDate(state.timetableWeekStart);
  if (state.viewMode === 'DAY') {
    state.timetableDays = [getTimetableDay(state.selectedDay)];
  } else {
    state.timetableDays = getTimetableWeekDays(state.timetableWeekStart);
  }
}

function setDayMode(state: TimetableState) {
  if (!state.selectedDay) {
    state.selectedDay = getDefaultSelectedDay(state.timetableWeekStart);
  }
  if (state.selectedDay) {
    state.timetableDays = [getTimetableDay(state.selectedDay)];
    state.viewMode = 'DAY';
  }
}

function getDefaultSelectedDay(weekStart: string) {
  const weekStartDate = parseWeekStart(weekStart);
  const isThisWeekSelected =
    weekStartDate.diffNow('days').days > -7 &&
    weekStartDate.diffNow('days').days <= 0;
  return toSelectedDayFormat(
    isThisWeekSelected ? DateTime.now() : weekStartDate,
  );
}

// Utils

function getDefaults() {
  const timetableWeekStart = pipe(
    startOf('week'),
    toWeekStartFormat,
  )(DateTime.now());
  const calendarMonthStart = pipe(
    startOf('month'),
    toMonthStartFormat,
  )(DateTime.now());
  const timetableWeekNumber = getWeekStartDateNumber(timetableWeekStart);
  const selectedDay = getDefaultSelectedDay(timetableWeekStart);
  return {
    timetableWeekStart,
    calendarMonthStart,
    timetableWeekNumber,
    selectedDay,
  };
}

export const {
  setTimetableId,
  changeViewMode,
  selectCalendarDay,
  selectTimetableWeek,
  setTimetableWeekNumber,
  refreshCategories,
  setCalendarVisibility,
  setIcsUrlModalVisibility,
  toggleCategoryVisibility,
  adjustTimetableDay,
  adjustTimetableWeek,
  adjustCalendarMonth,
  setTimezone,
  setIsRedirectedToCourseTimetable,
  setIsCurrentPersonalTimetableAvailable,
} = timetableSlice.actions;

export const timetableReducer = timetableSlice.reducer;
export default timetableSlice.reducer;
